import { lazy, ComponentType, LazyExoticComponent } from 'react';

const CHUNK_RELOAD_KEY = 'chunk-reload-attempted';

/**
 * Wraps React.lazy() with a reload-once fallback for stale chunk hashes.
 *
 * After a new deploy, old chunk filenames no longer exist on the CDN. Users
 * with a cached index.html request those old filenames and get 404s. This
 * wrapper detects that failure, reloads the page once (getting the fresh
 * index.html), and clears the sessionStorage flag so subsequent navigations
 * can retry if needed.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() =>
    factory()
      .then((module) => {
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
        return module;
      })
      .catch((err: Error) => {
        const isChunkError =
          err?.message?.includes('Failed to fetch dynamically imported module') ||
          err?.message?.includes('Importing a module script failed') ||
          err?.name === 'ChunkLoadError';

        if (isChunkError && !sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, 'true');
          window.location.reload();
          return new Promise<never>(() => {});
        }
        throw err;
      }),
  );
}
